/**
 * Derive a friendly default org name from the signed-in user so the
 * create-org form is near one-click after the user picks "Create" on
 * the welcome screen. Prefers the display name, falls back to the OAuth
 * login, and finally to the email's local-part. Anything resembling an
 * email or a login with special characters is normalised so the
 * suggestion reads as a plain workspace label.
 */
export function defaultOrgNameFromUser(
  user: { name?: string | null; login?: string | null; email?: string | null } | null,
): string {
  if (!user) return '';
  const raw = user.name?.trim() || user.login?.trim() || user.email?.split('@')[0]?.trim() || '';
  if (!raw) return '';
  // First whitespace-delimited token, capitalised. Avoids a long
  // display name like "Alice Smith" producing "Alice Smith's
  // Workspace" — workspace labels read better as a single word.
  const firstWord = raw.split(/\s+/)[0] ?? raw;
  const cleaned = firstWord.replace(/[^A-Za-z0-9]/g, '');
  if (!cleaned) return '';
  const capitalised = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  return `${capitalised}'s Workspace`;
}

/**
 * Derive a clean URL slug from an org name. The server's slugify replaces
 * every non-`[a-z0-9-]` character with a dash, so a friendly possessive
 * name like "Alice's Workspace" would otherwise become the awkward
 * `alice-s-workspace`. Stripping the possessive `'s` first yields the much
 * nicer `alice-workspace`. Pre-filling the slug field with this default
 * spares users the bad auto-derivation while keeping the suggestion fully
 * editable.
 */
export function defaultOrgSlugFromName(name: string): string {
  return name
    .toLowerCase()
    .replace(/['’]s\b/g, '') // drop possessive "'s" before slugifying
    .replace(/['’]/g, '') // drop any remaining apostrophes (don't dash them)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}
